import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Clock, X } from "lucide-react";
import { getWorkHoursTimeSlots } from "../../lib/workingHours";

interface TimePickerProps {
  value: string | null;
  onChange: (time: string | null) => void;
  ariaLabel?: string;
  // When true, only show slots that fall inside the working-hours window
  // (Mon–Fri 09:00–16:45). The dialog flips this on for tasks/subtasks with
  // working_hours_only set, so the invalid slots don't even appear.
  restrictToWorkingHours?: boolean;
}

/** Generate time slots every 15 minutes, 00:00–23:45 */
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      );
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();
const WORK_HOURS_SLOTS = getWorkHoursTimeSlots();

/** Convert "HH:MM" to display format like "9:00 AM" */
function formatTime12h(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${suffix}`;
}

/** Segment label for the group header */
function getSegmentLabel(time24: string): string {
  const h = parseInt(time24.split(":")[0], 10);
  if (h < 6) return "Night";
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  ariaLabel = "Scheduled time",
  restrictToWorkingHours = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const [focusIndex, setFocusIndex] = useState(-1);

  const slots = useMemo(
    () => (restrictToWorkingHours ? WORK_HOURS_SLOTS : TIME_SLOTS),
    [restrictToWorkingHours],
  );

  // Find the nearest slot index for the current value
  const selectedIndex = value
    ? slots.indexOf(value) !== -1
      ? slots.indexOf(value)
      : // Find nearest slot for non-15-min values
        slots.reduce((best, slot, i) => {
          const diff = Math.abs(
            toMinutes(slot) - toMinutes(value)
          );
          const bestDiff = Math.abs(
            toMinutes(slots[best]) - toMinutes(value)
          );
          return diff < bestDiff ? i : best;
        }, 0)
    : -1;

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (open && listRef.current) {
      // Small delay to let the popover render
      requestAnimationFrame(() => {
        if (selectedRef.current) {
          selectedRef.current.scrollIntoView({ block: "center" });
        } else if (listRef.current) {
          // Default to ~9:00 AM area if nothing selected
          const target = listRef.current.querySelector(
            '[data-time="09:00"]'
          );
          target?.scrollIntoView({ block: "center" });
        }
      });
    }
    if (open) {
      setFocusIndex(selectedIndex >= 0 ? selectedIndex : -1);
    }
  }, [open, selectedIndex]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Keyboard navigation in the list
  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((prev) =>
          prev < slots.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter" && focusIndex >= 0) {
        e.preventDefault();
        onChange(slots[focusIndex]);
        setOpen(false);
      }
    },
    [focusIndex, onChange, slots]
  );

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-index="${focusIndex}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [focusIndex, open]);

  // Track segment headers for visual grouping
  let lastSegment = "";

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button styled like the existing inputs */}
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-warm-blue transition-colors"
      >
        <Clock
          className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
          size={16}
        />
        <span
          className={
            value ? "text-warm-text-primary" : "text-warm-text-tertiary"
          }
        >
          {value ? formatTime12h(value) : "Set time"}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear time"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onChange(null);
                setOpen(false);
              }
            }}
            className="ml-auto p-0.5 rounded-full hover:bg-warm-bg-base text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
          >
            <X size={14} />
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Select time"
          tabIndex={0}
          onKeyDown={handleListKeyDown}
          className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-warm-bg-card border border-warm-border rounded-soft shadow-soft-lg overflow-y-auto animate-popover-in origin-top"
          style={{ maxHeight: "240px" }}
        >
          {slots.map((slot, i) => {
            const segment = getSegmentLabel(slot);
            const showHeader = segment !== lastSegment;
            lastSegment = segment;
            const isSelected = slot === value;
            const isFocused = i === focusIndex;

            return (
              <React.Fragment key={slot}>
                {showHeader && (
                  <div className="sticky top-0 px-3 py-1.5 text-[10px] font-medium text-warm-text-tertiary uppercase tracking-wider bg-warm-bg-card/95 backdrop-blur-sm border-b border-warm-border-subtle">
                    {segment}
                  </div>
                )}
                <button
                  ref={isSelected ? selectedRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-time={slot}
                  data-index={i}
                  onClick={() => {
                    onChange(slot);
                    setOpen(false);
                  }}
                  onMouseEnter={() => setFocusIndex(i)}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                    isSelected
                      ? "bg-warm-blue/10 text-warm-blue font-medium"
                      : isFocused
                        ? "bg-warm-bg-elevated text-warm-text-primary"
                        : "text-warm-text-primary hover:bg-warm-bg-elevated"
                  }`}
                >
                  {formatTime12h(slot)}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Convert "HH:MM" to total minutes */
function toMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

export default TimePicker;
